import { Chip, Divider, Stack, Typography } from '@mui/material'
import numeral from 'numeral'
import { useMemo } from 'react'

import {
  Maybe,
  PreSaleLotteryConfirmationFragment,
} from '@/clients/utoniq-core/schema'
import { ThroughTicketTag } from '@/components/organisms'
import { ApplyPreSaleLotteryValue } from '@/components/pages/LotteryDetail/template/LotteryDetailForm'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

type FirstComeFirstServedPlansCellProps = {
  choice: ApplyPreSaleLotteryValue | undefined
  items: PreSaleLotteryConfirmationFragment['items'] | undefined
}
export const FirstComeFirstServedPlansCell: Cell<FirstComeFirstServedPlansCellProps> =
  ({ choice, items }) => {
    const { t } = useTranslation('firstComeFirstServedConfirmation')
    return (
      <CellFrame sx={{ pb: 5 }}>
        <Stack sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}>
          <Typography variant="h5" color="text.secondary">
            {t('purchaseInfo', '購入情報')}
          </Typography>
        </Stack>
        <Stack spacing={4} sx={{ pt: 3 }}>
          {choice?.choices?.map(({ itemId, skuId }, index) => (
            <ItemSelectedCell
              item={items?.find(item => item.id === itemId)}
              skuId={skuId}
              choiceLength={choice?.choices?.length}
              key={skuId ?? itemId}
              count={choice.purchaseCountChoices[index]?.count}
              index={index + 1}
            />
          ))}
        </Stack>
      </CellFrame>
    )
  }

type ItemSelectedCellProps = {
  item?: PreSaleLotteryConfirmationFragment['items'][0]
  skuId?: string
  count?: number
  billingStatement?: {
    label: Maybe<string> | undefined
    value: string
  }[]
  choiceLength?: number
  index: number
}
const ItemSelectedCell: Cell<ItemSelectedCellProps> = ({
  item,
  skuId,
  count,
  choiceLength = 1,
  index,
}) => {
  const { t } = useTranslation('firstComeFirstServedConfirmation')
  const skuUnit = useMemo(() => {
    if (!skuId) return undefined
    return item?.skuSettings?.units?.find(s => s.id === skuId)
  }, [skuId, item?.skuSettings?.units])

  return (
    <CellFrame>
      {choiceLength > 1 && (
        <Chip
          variant="outlined"
          label={
            <Typography variant="overline">
              {t('preference', { index })}
            </Typography>
          }
          sx={{
            bgcolor: 'white',
            p: 0,
            height: 20,
            '.MuiChip-label': { p: 1 },
          }}
        />
      )}
      <Stack spacing={1} sx={{ py: 2 }}>
        <Typography variant="subtitle1" color="text.secondary">
          {t('selectionTicket', '選択チケット')}
        </Typography>
        <Typography variant="body2">{item?.title}</Typography>
        <Stack direction="row" alignItems="center" spacing={2}>
          {skuUnit?.unitName && (
            <Typography variant="body2">{skuUnit.unitName}</Typography>
          )}
          {item?.metadata?.ticket?.isThroughTicket && <ThroughTicketTag />}
        </Stack>
      </Stack>
      <Divider />
      <Stack spacing={1} sx={{ py: 2 }}>
        <Typography variant="subtitle1" color="text.secondary">
          {t('ticketDetail', 'チケット詳細')}
        </Typography>
        <Typography variant="body2">
          {item?.metadata?.ticket?.description}
        </Typography>
      </Stack>
      <Divider />
      <Stack spacing={1} sx={{ py: 2 }}>
        <Typography variant="subtitle1" color="text.secondary">
          {t('price', '価格')}
        </Typography>
        <Typography variant="body2">
          {t('pricePerSheet', {
            price: numeral(skuUnit?.price || item?.price).format('0,0'),
          })}
        </Typography>
      </Stack>
      <Divider />
      <Stack spacing={1} sx={{ py: 2 }}>
        <Typography variant="subtitle1" color="text.secondary">
          {t('quantity', '購入枚数')}
        </Typography>
        <Typography variant="body2">
          {count}
          {t('sheet')}
        </Typography>
      </Stack>
    </CellFrame>
  )
}
